import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { updateProfile as updateProfileApi } from '../services/shopApi'

const API = '/api/v1'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('access_token'))
  const [loading, setLoading] = useState(true)
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false)

  // Fetch current user with a given token
  const fetchMe = useCallback(async (token) => {
    const res = await fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Unauthorized')
    return res.json()
  }, [])

  // On mount: validate stored token
  useEffect(() => {
    if (!accessToken) {
      setLoading(false)
      return
    }
    fetchMe(accessToken)
      .then(setUser)
      .catch(() => {
        // Try refresh
        const refresh = localStorage.getItem('refresh_token')
        if (!refresh) {
          clearTokens()
          setLoading(false)
          return
        }
        refreshTokens(refresh).finally(() => setLoading(false))
      })
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function saveTokens(data) {
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)
    setAccessToken(data.access_token)
  }

  function clearTokens() {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    setAccessToken(null)
    setUser(null)
  }

  async function refreshTokens(refreshToken) {
    try {
      const res = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      if (!res.ok) throw new Error('Refresh failed')
      const data = await res.json()
      saveTokens(data)
      const me = await fetchMe(data.access_token)
      setUser(me)
    } catch {
      clearTokens()
    }
  }

  async function register(data) {
    const res = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || err.message || 'Registration failed')
    }
    const tokenData = await res.json()
    saveTokens(tokenData)
    const me = await fetchMe(tokenData.access_token)
    setUser(me)
    return me
  }

  const WEAK_PASSWORDS = ['qwer1234', 'password', '12345678', 'fasttrack']

  async function login(email, password) {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || err.message || 'Login failed')
    }
    const data = await res.json()
    saveTokens(data)
    const me = await fetchMe(data.access_token)
    if (me.roles?.includes('fasttrackban')) {
      clearTokens()
      throw new Error('Account is banned')
    }
    setUser(me)
    if (WEAK_PASSWORDS.includes(password)) setNeedsPasswordChange(true)
    return me
  }

  function logout() {
    clearTokens()
  }

  async function updateProfile(data) {
    const updated = await updateProfileApi(accessToken, data)
    setUser(updated)
    return updated
  }

  return (
    <AuthContext.Provider value={{ user, accessToken, loading, login, logout, register, updateProfile, needsPasswordChange, dismissPasswordChange: () => setNeedsPasswordChange(false) }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
